import { addDays, daysBetween } from "./date";
import { dayKcal, sessionKcal } from "./energy";
import { foodOn } from "./intake";
import { doseIndex, sessionKeyFor, typeName } from "./plan";
import { dayQueue, queueMinutes, sessionMinutes, sessionPct } from "./queue";
import { sessionElapsedMs } from "./session";
import { dayFull } from "./stats";
import { hasTreadmill, tmProtocol, tmProtocolLine, tmTotalSecs, tmTotals } from "./treadmill";
import { walkKcal } from "./walk";
import { sessionGate } from "./trainingQuestions";
import type { DeviceAnswers } from "./eating";
import type { ActiveSession, KayaState, SessionKey } from "./types";

/**
 * What the Today screen leads with: the one thing to do next.
 *
 * First matching rule wins. An open session outranks everything because a second Start would
 * begin a second session over the one the phone's counter is still holding (ADR-027).
 */
export type NextUp =
  | { kind: "session-open"; session: ActiveSession; elapsedMs: number }
  | {
      kind: "session";
      key: SessionKey;
      name: string;
      minutes: number;
      moves: number;
      kcalEst: number;
      pct: number;
    }
  | { kind: "treadmill"; minutes: number; protocolLine: string; kcalEst: number }
  | { kind: "done" }
  | { kind: "rest" };

/** `device` is `useDeviceAnswers()`: the safety answers swap or stop the day's session and the
 *  treadmill's brisk phases as Train does (`trainingQuestions.ts::sessionGate`). */
export function nextUp(
  S: KayaState,
  ds: string,
  session: ActiveSession | null,
  now: number,
  device: DeviceAnswers = {},
  simple?: boolean
): NextUp {
  if (session) return { kind: "session-open", session, elapsedMs: sessionElapsedMs(session, now) };

  const L = S.logs[ds];
  const gate = sessionGate(S, sessionKeyFor(S, ds), device, new Date(now));
  if (gate.stop) return { kind: "rest" };
  const key = gate.key;
  const pct = sessionPct(S, ds, key);
  // A period day kept as rest keeps the streak; offering the session back would undo that choice.
  if (key !== "R" && pct !== null && !L?.periodSkip && !dayFull(S, ds)) {
    // the queue the day runs: frozen at its first tick, today's before it (ADR-050)
    const dq = dayQueue(S, ds, key);
    return {
      kind: "session",
      key,
      name: typeName(key),
      minutes: dq.queue ? queueMinutes(S, dq.queue, simple) : sessionMinutes(S, key, doseIndex(S, key, ds), undefined, simple),
      moves: dq.queue ? dq.queue.filter((q) => q.kind !== "rest").length : dq.ran.n,
      kcalEst: sessionKcal(S, key, undefined, dq.queue ?? undefined),
      pct,
    };
  }

  if (hasTreadmill(S) && !L?.treadmill) {
    const phases = tmProtocol(S, gate.easyTreadmill);
    return {
      kind: "treadmill",
      minutes: Math.round(tmTotalSecs(phases) / 60),
      protocolLine: tmProtocolLine(S, gate.easyTreadmill),
      kcalEst: tmTotals(S, phases, phases.length).kcal,
    };
  }

  if (dayFull(S, ds) || L?.treadmill) return { kind: "done" };
  return { kind: "rest" };
}

export interface WorkParts {
  session: number | null;
  treadmill: number | null;
  walk: number | null;
  total: number | null;
}

/**
 * The day's work kcal, split by where it came from.
 *
 * Null means nothing of that kind was logged, which is a different answer from a logged 0
 * (invariant 11). The total is `dayKcal`, and the session part is whatever of it the treadmill
 * and walks do not account for, so the parts always add up to the figure shown elsewhere.
 */
export function workParts(S: KayaState, ds: string): WorkParts {
  const L = S.logs[ds];
  if (!L) return { session: null, treadmill: null, walk: null, total: null };

  const key = L.sessionKey || sessionKeyFor(S, ds);
  const sessionLogged = !!(L.kcal || L.extraKcal || sessionPct(S, ds, key));
  const treadmill = L.treadmill ? Math.round(L.treadmill.kcal || 0) : null;
  const walk = L.walks && L.walks.length ? Math.round(walkKcal(L.walks)) : null;

  if (!sessionLogged && treadmill === null && walk === null)
    return { session: null, treadmill: null, walk: null, total: null };

  const total = dayKcal(S, ds);
  const session = sessionLogged ? Math.max(0, total - (treadmill ?? 0) - (walk ?? 0)) : null;
  return { session, treadmill, walk, total };
}

export type SetupStepId = "body" | "steps" | "treadmill" | "food";

export interface SetupStep {
  id: SetupStepId;
  done: boolean;
}

/**
 * The first-run checklist.
 *
 * Answers are read from evidence in the state rather than from the profile's flags, because
 * `hasTreadmill` and `foodOn` default to "yes" and a stored `true` may predate the form writing
 * them only when touched: it does not say the question was ever put to the person. A "no" does,
 * since nothing writes one unasked. `treadmillAnswered` is the device's own record of the
 * question being answered (`LocalMeta.setupTreadmillAnswered`).
 */
export function setupSteps(
  S: KayaState,
  ds: string,
  opts: { treadmillAnswered?: boolean } = {}
): SetupStep[] {
  const p = S.profile;
  const weighed = p.startWeight > 0 || S.measures.some((m) => !!m.w);
  const body = p.heightCm > 0 && weighed && !!p.birthYear;

  // A count posted by the phone this week is the only proof a counter is set up; the API keys
  // that would also say so live on the server, not in the state.
  const since = addDays(ds, -7);
  const steps = Object.entries(S.logs).some(
    ([d, L]) => d >= since && d <= ds && (L.deviceSteps || 0) > 0
  );

  const treadmill =
    p.hasTreadmill === false ||
    !!opts.treadmillAnswered ||
    (p.programs?.length ?? 0) > 0 ||
    Object.values(S.logs).some((L) => !!L.treadmill);

  // Opting out answers the question as well as logging does.
  const food = !foodOn(S) || Object.values(S.logs).some((L) => (L.food || []).length > 0);

  return [
    { id: "body", done: body },
    { id: "steps", done: steps },
    { id: "treadmill", done: treadmill },
    { id: "food", done: food },
  ];
}

export interface WeighInDue {
  due: boolean;
  lastDs: string | null;
  daysAgo: number | null;
}

/** Due when nothing was weighed in the seven days ending `ds`. Future-dated entries are ignored. */
export function weighInDue(S: KayaState, ds: string): WeighInDue {
  let lastDs: string | null = null;
  for (const m of S.measures) {
    if (!m.w || m.date > ds) continue;
    if (lastDs === null || m.date > lastDs) lastDs = m.date;
  }
  if (lastDs === null) return { due: true, lastDs: null, daysAgo: null };
  const daysAgo = daysBetween(lastDs, ds);
  return { due: daysAgo >= 7, lastDs, daysAgo };
}
